import { useCallback } from 'react';
import config from '../../config';
import { buildBookingLeadBody, createBookingLead } from '../../utils/bookingLeadsApi';

const API = config.customerApiBase;

export function useLeadsApi() {
  const list = useCallback(async ({ view, status, search, source, leadType, region, regionLocation, contactByFrom, contactByTo, sortBy, sortOrder, excludeContactByToday, page = 1, pageSize = 20 } = {}) => {
    const params = new URLSearchParams();
    if (view) params.append('view', view);
    if (status && !view) params.append('status', status);
    if (search) params.append('search', search);
    if (source) params.append('source', source);
    if (leadType) params.append('lead_type', leadType);
    if (region) params.append('region', region);
    if (regionLocation) params.append('region_location', regionLocation);
    if (contactByFrom) params.append('contact_by_after', contactByFrom);
    if (contactByTo) params.append('contact_by_before', contactByTo);
    if (excludeContactByToday) params.append('exclude_contact_by_today', 'true');
    if (sortBy) {
      params.append('sort_by', sortBy);
      if (sortOrder) {
        params.append('sort_order', sortOrder);
      } else if (sortBy === 'contact_by' || sortBy === 'follow_up_at') {
        params.append('sort_order', 'asc');
      }
    }
    params.append('limit', String(pageSize));
    params.append('offset', String((page - 1) * pageSize));
    const res = await fetch(`${API}/booking-leads?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return {
      leads: data.leads || [],
      total: data.total || 0,
      locationCounts: data.location_counts || [],
      regionTotal: data.region_total || 0,
    };
  }, []);

  const get = useCallback(async (id) => {
    const res = await fetch(`${API}/booking-leads/${id}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, []);

  const update = useCallback(async (id, body) => {
    const res = await fetch(`${API}/booking-leads/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
  }, []);

  const setStatus = useCallback(async (id, status, convertedBookingId, droppedReason, pricing = {}) => {
    const body = { status, converted_booking_id: convertedBookingId || null, ...pricing };
    if (droppedReason && droppedReason.trim()) body.dropped_reason = droppedReason.trim();
    const res = await fetch(`${API}/booking-leads/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update status');
    }
    return res.json();
  }, []);

  const scheduleFollowUp = useCallback(async (id, followUpAt, noteText) => {
    const body = { follow_up_at: followUpAt };
    if (noteText && noteText.trim()) body.note_text = noteText.trim();
    const res = await fetch(`${API}/booking-leads/${id}/follow-up`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to schedule follow-up');
    }
    return res.json();
  }, []);

  const addNote = useCallback(async (leadId, text) => {
    const res = await fetch(`${API}/booking-leads/${leadId}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note_text: text }),
    });
    if (!res.ok) throw new Error('Failed to add note');
    return res.json();
  }, []);

  const listNotes = useCallback(async (leadId) => {
    const res = await fetch(`${API}/booking-leads/${leadId}/notes`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.notes || [];
  }, []);

  const listActivities = useCallback(async (leadId) => {
    const res = await fetch(`${API}/booking-leads/${leadId}/activities`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.activities || [];
  }, []);

  const listConversation = useCallback(async (lead) => {
    const conversationId = lead?.conversation_id;
    if (!conversationId) return null;
    try {
      const res = await fetch(`${API}/conversations/${conversationId}`);
      if (!res.ok) return null;
      const data = await res.json();
      return data?.conversation || null;
    } catch {
      return null;
    }
  }, []);

  const getCounts = useCallback(async () => {
    const res = await fetch(`${API}/booking-leads/count?group=true`);
    if (!res.ok) return { counts: {}, due: 0 };
    const data = await res.json();
    return { counts: data.counts || {}, due: data.due || 0, new_without_due: data.new_without_due ?? 0 };
  }, []);

  const searchBookings = useCallback(async (query) => {
    const params = new URLSearchParams();
    if (query && query.trim()) params.append('search', query.trim());
    params.append('limit', '10');
    const res = await fetch(`${API}/bookings?${params.toString()}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.bookings || [];
  }, []);

  const getRecentBookings = useCallback(async (phone, limit = 5) => {
    const params = new URLSearchParams();
    const phoneStr = String(phone ?? '').trim();
    if (phoneStr) params.append('phone', phoneStr);
    params.append('limit', String(limit));
    const res = await fetch(`${API}/bookings?${params.toString()}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.bookings || [];
  }, []);

  const getRecommendedBookings = useCallback(async (leadId, limit = 5) => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    const res = await fetch(`${API}/booking-leads/${leadId}/recommended-bookings?${params.toString()}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.bookings || [];
  }, []);

  const create = useCallback(async (body) => createBookingLead(body), []);

  const getDueCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/booking-leads/count?due=true`);
      if (!res.ok) return 0;
      const data = await res.json();
      return data.count || 0;
    } catch {
      return 0;
    }
  }, []);

  const triggerOpsCall = useCallback(async ({ customerPhone, customerId }) => {
    const token = localStorage.getItem('token') || '';
    const res = await fetch(`${config.opsApiBase}/call-customer`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_phone: customerPhone,
        customer_id: customerId || null,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to place call');
    return data;
  }, []);

  const snooze = useCallback(async (lead, days) => {
    const base = lead.contact_by ? new Date(lead.contact_by) : new Date();
    base.setDate(base.getDate() + days);
    return update(lead.id, { contact_by: base.toISOString() });
  }, [update]);

  return {
    list, get, update, setStatus, scheduleFollowUp, addNote,
    listNotes, listActivities, listConversation, getCounts, getDueCount,
    triggerOpsCall, snooze, create, searchBookings, getRecentBookings, getRecommendedBookings,
  };
}
